import { apiRequest } from "@nusrif/utils";
import { GetUserDataResponse, UpdateUserInput, User } from "@nusrif/types";
import { createCart, getCartFromServer } from "./cart-services";

export function setUser(user: User) {
    localStorage.setItem('user', JSON.stringify(user));
}

export function getUser(): User | null {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
}

export function removeUser() {
    localStorage.removeItem('user');
}

export function isUserOnboarded(): boolean {
    const user = getUser();
    return user ? !!(user.firstName && user.lastName && user.email) : false;
}

// Brand colors for avatar backgrounds
const BRAND_COLORS = [
    'bg-primary-blue',    // rgb(30 74 168)
    'bg-secondary-yellow', // rgb(255 199 0)
];

/**
 * Generate initials from user's name
 */
export function getUserInitials(user: User): string {
    if (user.firstName && user.lastName) {
        return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    
    if (user.firstName) {
        return user.firstName.charAt(0).toUpperCase();
    }
    
    if (user.username) {
        return user.username.charAt(0).toUpperCase();
    }
    
    return 'U';
}

/**
 * Get a consistent brand color for a user based on their ID
 */
export function getUserAvatarColor(user: User): string {
    if (!user.id) return BRAND_COLORS[0];
    
    // Use the user's ID to consistently assign a color
    const colorIndex = user.id.charCodeAt(0) % BRAND_COLORS.length;
    return BRAND_COLORS[colorIndex];
}

/**
 * Get avatar display data for a user
 */
export function getUserAvatarData(user: User) {
    return {
        initials: getUserInitials(user),
        backgroundColor: getUserAvatarColor(user),
    };
}

export async function getUserFromServer(): Promise<GetUserDataResponse> {
    const userData = await apiRequest<GetUserDataResponse>(`/users/${getUser()?.id}`, {
        method: 'GET',
    });

    if(userData.success && userData.data.user && userData.data.user.storeCustomerId) {
        setUser(userData.data.user);

        // Sync cart with server
        if(userData.data.user.cartId) {
           getCartFromServer();
        } else {
            createCart();
        }
    }

    return userData;
}

export async function updateUser(user: UpdateUserInput): Promise<GetUserDataResponse> {
    const userId = getUser()?.id;
    const userData = await apiRequest<GetUserDataResponse>(`/users/${userId}`, {
        method: 'PUT',
        body: user,
    });

    if(userData.success && userData.data.user) {
        setUser(userData.data.user);
    }

    return userData;
}